import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { pickDetail } from '../lib/data'
import { useAuth } from '../hooks/useAuth'

const TIER_STYLE = {
  'Critical': { bg: '#FCEBEB', color: '#A32D2D', border: '#f09595', dot: '🔴' },
  'High':     { bg: '#FAEEDA', color: '#854F0B', border: '#EF9F27', dot: '🟡' },
  'Medium':   { bg: '#EAF3DE', color: '#3B6D11', border: '#97C459', dot: '🟢' },
  'Low':      { bg: '#F1EFE8', color: '#5F5E5A', border: '#D3D1C7', dot: '⚪' },
}

function PrintStyles() {
  return (
    <style>{`
      @media print {
        .no-print { display: none !important; }
        .app-shell > nav { display: none !important; }
        .topnav { display: none !important; }
        .bottom-nav { display: none !important; }
        body { background: white; }
        .main-content { padding: 0; max-width: 100%; }
        .page-break { page-break-before: always; }
        .card { border: 1px solid #ddd !important; box-shadow: none !important; }
      }
    `}</style>
  )
}

export default function Reports() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [sites, setSites] = useState([])
  const [selectedSite, setSelectedSite] = useState(null)
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [reportLoading, setReportLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [filterTier, setFilterTier] = useState('All')

  useEffect(() => {
    if (profile?.role !== 'admin') { navigate('/'); return }
    fetchSites()
  }, [profile])

  async function fetchSites() {
    const { data } = await supabase
      .from('sites')
      .select('id, name, city, state, site_details(priority_tier, priority_score, rank, lpr_issue, gate_issue, district, site_code)')
      .eq('active', true)
      .order('name')
    const sorted = (data || []).sort((a, b) => {
      const ra = pickDetail(a.site_details)?.rank ?? 9999
      const rb = pickDetail(b.site_details)?.rank ?? 9999
      return ra - rb
    })
    setSites(sorted)
    setLoading(false)
  }

  async function loadReport(site) {
    setSelectedSite(site)
    setReport(null)
    setReportLoading(true)

    // Get all audits for this site
    const { data: audits } = await supabase
      .from('audits')
      .select('id, status, created_at, submitted_at, notes, profiles(full_name)')
      .eq('site_id', site.id)
      .order('created_at', { ascending: false })

    if (!audits || audits.length === 0) {
      setReport({ audits: [], categories: [], questions: [] })
      setReportLoading(false)
      return
    }

    // Get categories and questions
    const [catsRes, qsRes] = await Promise.all([
      supabase.from('categories').select('*').eq('active', true).order('sort_order'),
      supabase.from('questions').select('*').eq('active', true).order('sort_order'),
    ])

    // Get all answers + photos for all audits at this site
    const auditIds = audits.map(a => a.id)
    const { data: answers } = await supabase
      .from('audit_answers')
      .select('*, photos(id, storage_path)')
      .in('audit_id', auditIds)

    // Build photo URLs
    const answersWithPhotos = await Promise.all((answers || []).map(async ans => {
      if (!ans.photos?.length) return { ...ans, photoUrls: [] }
      const urls = await Promise.all(ans.photos.map(async p => {
        const { data } = await supabase.storage.from('audit-photos').createSignedUrl(p.storage_path, 3600)
        return data?.signedUrl || ''
      }))
      return { ...ans, photoUrls: urls }
    }))

    // Group answers by audit_id then question_id
    const answersByAudit = {}
    for (const ans of answersWithPhotos) {
      if (!answersByAudit[ans.audit_id]) answersByAudit[ans.audit_id] = {}
      answersByAudit[ans.audit_id][ans.question_id] = ans
    }

    setReport({
      audits,
      categories: catsRes.data || [],
      questions: qsRes.data || [],
      answersByAudit,
    })
    setReportLoading(false)
  }

  const tiers = ['All', 'Critical', 'High', 'Medium', 'Low']
  const filtered = sites.filter(s => {
    const tier = pickDetail(s.site_details)?.priority_tier || ''
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.city || '').toLowerCase().includes(search.toLowerCase())
    const matchTier = filterTier === 'All' || tier === filterTier
    return matchSearch && matchTier
  })

  if (loading) return <div className="spinner" />

  return (
    <div>
      <PrintStyles />

      {!selectedSite ? (
        // SITE PICKER
        <div>
          <div className="page-header">
            <div>
              <div className="page-title">Reports</div>
              <div className="page-subtitle">Select a site to view its consolidated audit report</div>
            </div>
            <button className="btn btn-primary" onClick={() => navigate('/reports/weekly')}>
              Weekly executive report →
            </button>
          </div>

          <input
            className="form-input"
            placeholder="Search by site name or city..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ marginBottom: 10 }}
          />

          <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
            {tiers.map(t => {
              const style = TIER_STYLE[t]
              const active = filterTier === t
              return (
                <button key={t} onClick={() => setFilterTier(t)} style={{
                  padding: '4px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 13,
                  border: `1px solid ${active && style ? style.color : 'var(--border)'}`,
                  background: active && style ? style.bg : 'var(--bg)',
                  color: active && style ? style.color : 'var(--text-muted)',
                  fontWeight: active ? 600 : 400,
                }}>
                  {style?.dot} {t}
                </button>
              )
            })}
          </div>

          {filtered.map(site => {
            const det = pickDetail(site.site_details)
            const tier = det?.priority_tier
            const ts = TIER_STYLE[tier]
            return (
              <div key={site.id} className="site-item" onClick={() => loadReport(site)}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div className="site-name">{site.name}</div>
                    {tier && (
                      <span style={{
                        fontSize: 11, padding: '2px 7px', borderRadius: 10,
                        background: ts?.bg, color: ts?.color, fontWeight: 500,
                      }}>
                        {ts?.dot} {tier}
                      </span>
                    )}
                  </div>
                  <div className="site-meta">
                    {[site.city, site.state].filter(Boolean).join(', ')}
                    {det?.district ? ` · ${det.district}` : ''}
                  </div>
                </div>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
                  style={{ width: 18, height: 18, color: 'var(--text-muted)', flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            )
          })}
        </div>
      ) : (
        // REPORT VIEW
        <div>
          <div className="no-print" style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-sm" onClick={() => { setSelectedSite(null); setReport(null) }}>
              ← All sites
            </button>
            <button className="btn btn-sm" onClick={() => window.print()}>
              🖨️ Print / Save PDF
            </button>
          </div>

          {reportLoading ? (
            <div className="spinner" />
          ) : (
            <SiteReport site={selectedSite} report={report} />
          )}
        </div>
      )}
    </div>
  )
}

function SiteReport({ site, report }) {
  const det = pickDetail(site.site_details)
  const tier = det?.priority_tier
  const ts = TIER_STYLE[tier]

  if (!report) return null

  if (report.audits.length === 0) {
    return (
      <div>
        <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{site.name}</h1>
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <h3>No audits yet</h3>
          <p>No audits have been submitted for this site.</p>
        </div>
      </div>
    )
  }

  // Most recent submitted audit, fallback to most recent
  const primaryAudit = report.audits.find(a => a.status === 'submitted') || report.audits[0]
  const primaryAnswers = report.answersByAudit[primaryAudit.id] || {}

  // Stats for primary audit
  const allAnswerVals = Object.values(primaryAnswers)
  const passCount = allAnswerVals.filter(a => a.result === 'pass').length
  const failCount = allAnswerVals.filter(a => a.result === 'fail').length
  const naCount   = allAnswerVals.filter(a => a.result === 'na').length
  const totalQ    = report.questions.length
  const pct = totalQ ? Math.round((passCount + failCount + naCount) / totalQ * 100) : 0

  return (
    <div>
      {/* Report Header */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{site.name}</h1>
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
              {[site.city, site.state].filter(Boolean).join(', ')}
              {det?.district ? ` · ${det.district}` : ''}
            </div>
          </div>
          {tier && (
            <div style={{
              background: ts.bg, color: ts.color, border: `1px solid ${ts.border}`,
              borderRadius: 8, padding: '6px 14px', fontWeight: 600, fontSize: 14,
            }}>
              {ts.dot} {tier} Priority {det?.priority_score ? `· Score: ${det.priority_score}` : ''}
            </div>
          )}
        </div>

        {/* Site details row */}
        {det && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px 20px', padding: '12px 0', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', marginBottom: 14 }}>
            {det.site_code && <DetailCell label="Site code" value={det.site_code} />}
            {det.camera_location && <DetailCell label="Camera location" value={det.camera_location} />}
            <DetailCell label="LPR issue" value={det.lpr_issue === true ? '⚠️ Yes' : det.lpr_issue === false ? '✓ No' : '—'} valueColor={det.lpr_issue ? '#A32D2D' : '#3B6D11'} />
            <DetailCell label="Gate issue" value={det.gate_issue === true ? '⚠️ Yes' : det.gate_issue === false ? '✓ No' : '—'} valueColor={det.gate_issue ? '#A32D2D' : '#3B6D11'} />
            {det.pct_lp_manual != null && <DetailCell label="Manual LP entry" value={`${(det.pct_lp_manual * 100).toFixed(1)}%`} />}
          </div>
        )}

        {/* Audit meta */}
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, fontSize: 13, color: 'var(--text-muted)' }}>
          <span>
            Auditor: <strong style={{ color: 'var(--text)' }}>{primaryAudit.profiles?.full_name || 'Unknown'}</strong>
          </span>
          <span>
            Date: <strong style={{ color: 'var(--text)' }}>{new Date(primaryAudit.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
          </span>
          <span className={`badge ${primaryAudit.status === 'submitted' ? 'badge-pass' : 'badge-warn'}`}>
            {primaryAudit.status === 'submitted' ? 'Submitted' : 'In progress'}
          </span>
        </div>
      </div>

      {/* Score summary */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card">
          <div className="stat-number" style={{ color: 'var(--green)' }}>{passCount}</div>
          <div className="stat-label">Passed</div>
        </div>
        <div className="stat-card">
          <div className="stat-number" style={{ color: 'var(--red)' }}>{failCount}</div>
          <div className="stat-label">Failed</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{naCount}</div>
          <div className="stat-label">N/A</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{pct}%</div>
          <div className="stat-label">Completed</div>
        </div>
      </div>

      {/* Failures summary box */}
      {failCount > 0 && (
        <div className="card" style={{ borderColor: '#f09595', marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--red)', marginBottom: 12 }}>
            ⚠️ {failCount} Failure{failCount > 1 ? 's' : ''} Requiring Attention
          </h2>
          {report.categories.map(cat => {
            const catQs = report.questions.filter(q => q.category_id === cat.id)
            const catFails = catQs.filter(q => primaryAnswers[q.id]?.result === 'fail')
            if (!catFails.length) return null
            return (
              <div key={cat.id} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                  {cat.icon} {cat.name}
                </div>
                {catFails.map(q => {
                  const ans = primaryAnswers[q.id]
                  return (
                    <div key={q.id} style={{ paddingLeft: 12, marginBottom: 6 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>• {q.label}</div>
                      {ans?.notes && <div style={{ fontSize: 13, color: 'var(--red)', marginTop: 2, paddingLeft: 10 }}>{ans.notes}</div>}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      )}

      {/* Overall audit notes */}
      {primaryAudit.notes && (
        <div className="card" style={{ marginBottom: 20, borderColor: 'var(--blue-mid)' }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>📝 Overall Audit Notes</h2>
          <p style={{ fontSize: 14, lineHeight: 1.6 }}>{primaryAudit.notes}</p>
        </div>
      )}

      {/* Category-by-category breakdown */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Full Checklist Results</h2>
      {report.categories.map(cat => {
        const catQs = report.questions.filter(q => q.category_id === cat.id)
        const catAnswered = catQs.filter(q => primaryAnswers[q.id]?.result)
        const catPass = catQs.filter(q => primaryAnswers[q.id]?.result === 'pass').length
        const catFail = catQs.filter(q => primaryAnswers[q.id]?.result === 'fail').length
        const catPct = catQs.length ? Math.round(catAnswered.length / catQs.length * 100) : 0

        return (
          <div key={cat.id} className="card" style={{ marginBottom: 14 }}>
            {/* Category header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>{cat.icon}</span>
                <span style={{ fontSize: 16, fontWeight: 600 }}>{cat.name}</span>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {catFail > 0 && <span className="badge badge-fail">{catFail} fail</span>}
                {catPass > 0 && <span className="badge badge-pass">{catPass} pass</span>}
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{catPct}%</span>
              </div>
            </div>

            <div className="progress-bar" style={{ marginBottom: 14 }}>
              <div className="progress-fill" style={{ width: `${catPct}%` }} />
            </div>

            {/* Questions */}
            {catQs.map(q => {
              const ans = primaryAnswers[q.id]
              const result = ans?.result
              const hasPhotos = ans?.photoUrls?.length > 0
              const hasNotes = ans?.notes?.trim()

              return (
                <div key={q.id} style={{
                  padding: '12px 0',
                  borderBottom: '1px solid var(--border)',
                  borderLeft: result === 'fail' ? '3px solid #f09595' : result === 'pass' ? '3px solid #97C459' : '3px solid transparent',
                  paddingLeft: result ? 10 : 0,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{q.label}</div>
                      {q.hint && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{q.hint}</div>}
                      {hasNotes && (
                        <div style={{
                          marginTop: 6, fontSize: 13, padding: '6px 10px',
                          background: result === 'fail' ? '#FCEBEB' : 'var(--bg-secondary)',
                          borderRadius: 6, color: result === 'fail' ? 'var(--red)' : 'var(--text)',
                          lineHeight: 1.5,
                        }}>
                          💬 {ans.notes}
                        </div>
                      )}
                      {hasPhotos && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                          {ans.photoUrls.map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noreferrer">
                              <img src={url} alt={`evidence ${i+1}`} style={{
                                width: 80, height: 80, objectFit: 'cover',
                                borderRadius: 6, border: '1px solid var(--border)',
                              }} />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      {result ? (
                        <span className={`badge badge-${result}`}>
                          {result === 'na' ? 'N/A' : result.charAt(0).toUpperCase() + result.slice(1)}
                        </span>
                      ) : (
                        <span className="badge badge-na">—</span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )
      })}

      {/* Audit history */}
      {report.audits.length > 1 && (
        <div className="card" style={{ marginTop: 8, marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Audit History</h2>
          {report.audits.map((audit, i) => {
            const auditAnswers = Object.values(report.answersByAudit[audit.id] || {})
            const p = auditAnswers.filter(a => a.result === 'pass').length
            const f = auditAnswers.filter(a => a.result === 'fail').length
            return (
              <div key={audit.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < report.audits.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>
                    {new Date(audit.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {audit.profiles?.full_name || 'Unknown auditor'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span className="badge badge-pass">{p} pass</span>
                  {f > 0 && <span className="badge badge-fail">{f} fail</span>}
                  <span className={`badge ${audit.status === 'submitted' ? 'badge-pass' : 'badge-warn'}`}>
                    {audit.status === 'submitted' ? 'Submitted' : 'In progress'}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', marginTop: 24, paddingBottom: 16 }}>
        CarWash Audit Portal · Report generated {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </div>
    </div>
  )
}

function DetailCell({ label, value, valueColor }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 500, color: valueColor || 'var(--text)' }}>{value || '—'}</div>
    </div>
  )
}
